<template>
  <div v-if="poi" class="poi-quick-actions">
    <div class="poi-quick-actions__grid">
      <button type="button" class="poi-action-btn" @click="$emit('add-to-waypoints')">
        <span class="material-icons poi-action-btn__icon">add_location</span>
        <span class="poi-action-btn__label">Add to waypoints</span>
      </button>
      <button
        v-if="showAddControlPoint"
        type="button"
        class="poi-action-btn"
        @click="$emit('add-control-point')"
      >
        <span class="material-icons poi-action-btn__icon">{{ isActiveRouteEmpty ? 'flag' : 'place' }}</span>
        <span class="poi-action-btn__label">{{ isActiveRouteEmpty ? 'Set as start point' : 'Add control point' }}</span>
      </button>
      <a
        v-if="type === 'peak'"
        :href="`/peaks/${poi.id}/3d`"
        target="_blank"
        class="poi-action-btn"
      >
        <span class="material-icons poi-action-btn__icon">terrain</span>
        <span class="poi-action-btn__label">View in 3D</span>
      </a>
      <button
        v-if="showMakeHighlight"
        type="button"
        class="poi-action-btn"
        @click="$emit('make-hike-highlight')"
      >
        <span class="material-icons poi-action-btn__icon">star</span>
        <span class="poi-action-btn__label">Make hike highlight</span>
      </button>
      <button
        v-if="showSetStageEnd"
        type="button"
        class="poi-action-btn"
        :class="{ 'poi-action-btn--active': showStageDayPicker }"
        @click="$emit('set-as-stage-end')"
      >
        <span class="material-icons poi-action-btn__icon">sports_score</span>
        <span class="poi-action-btn__label">Set as stage end</span>
      </button>
    </div>

    <div v-if="showStageDayPicker && showSetStageEnd" class="poi-quick-actions__day-row">
      <label class="poi-quick-actions__day-label" for="poi-stage-day-select">End which day here</label>
      <select
        id="poi-stage-day-select"
        class="poi-quick-actions__day-select"
        @change="onDaySelected"
      >
        <option value="" disabled selected>Choose a day…</option>
        <option v-for="day in stageDays" :key="day.dayNumber" :value="day.dayNumber">
          {{ day.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

// Shared by both branches of MapDataPOIDetailDialog.vue (the plain fallback
// body and the SerpApi-enriched body) so this "quick action" card + day
// picker only needs to be styled and maintained in one place. Layout is
// modeled on the plan_trail guided-toolbar (bordered card, condensed equal-
// width grid, dropdown instead of a wall of buttons for the day picker).
export default defineComponent({
  name: 'PoiQuickActions',
  props: {
    poi: { type: Object, default: null },
    type: { type: String, default: '' },
    showAddControlPoint: { type: Boolean, default: false },
    isActiveRouteEmpty: { type: Boolean, default: false },
    showMakeHighlight: { type: Boolean, default: false },
    showSetStageEnd: { type: Boolean, default: false },
    showStageDayPicker: { type: Boolean, default: false },
    stageDays: { type: Array, default: () => [] },
  },
  emits: ['add-to-waypoints', 'add-control-point', 'make-hike-highlight', 'set-as-stage-end', 'attach-to-day'],
  methods: {
    onDaySelected(event) {
      const value = event.target.value;
      if (!value) return;
      this.$emit('attach-to-day', Number(value));
      event.target.value = '';
    },
  },
});
</script>

<style scoped>
/* Bordered "card" treatment, matching plan_trail's guided-toolbar. */
.poi-quick-actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin: 0.85rem 0;
  padding: 0.7rem 0.8rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* Condensed, equal-width grid — previously these were loose pills of
   varying widths that wrapped unevenly. */
.poi-quick-actions__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

.poi-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.6rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.76rem;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.poi-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.poi-action-btn__icon {
  font-size: 16px;
  flex-shrink: 0;
  color: #a1a4ad;
}

.poi-action-btn__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* "Set as stage end" while its day picker is open. */
.poi-action-btn--active {
  background: rgba(253, 223, 0, 0.14);
  border-color: rgba(253, 223, 0, 0.4);
  color: #fddf00;
}

.poi-action-btn--active .poi-action-btn__icon {
  color: #fddf00;
}

.poi-action-btn--active:hover {
  background: rgba(253, 223, 0, 0.22);
}

.poi-quick-actions__day-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.poi-quick-actions__day-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #a1a4ad;
  white-space: nowrap;
  flex-shrink: 0;
}

.poi-quick-actions__day-select {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.4rem 0.65rem;
  border-radius: 999px;
  border: 1px solid rgba(253, 223, 0, 0.35);
  background: rgba(253, 223, 0, 0.08);
  color: #fddf00;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
}

.poi-quick-actions__day-select:focus {
  outline: none;
  border-color: #fddf00;
}

.poi-quick-actions__day-select option {
  background: #17181d;
  color: #fff;
}
</style>
